import { Stack } from "@mui/material";
import * as React from "react";
import { useUpdateEffect } from "react-use";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { AutocompleteResultsContainer } from "../../containers/AutocompleteResultsContainer";
import { useMultiHangarState } from "../../containers/MultiHangarContainer";
import { useTowingEquipmentState } from "../../containers/TowingEquipmentContainer";
import { getStackPolygons } from "../../hooks/utils";
import { PlacementService } from "../../services/PlacementService";
import { Hangar, ParamSet, Preference, Stack as StackType } from "../../types";
import { convertAircraftPreferencesToAlgorithmPreferences } from "../../utils";
import { usePermissions } from "../../hooks/usePermissions";
import { Permission } from "../../utils/permissions";
import { Layout } from "../../widgets/Layout";
import { LayoutToolbar } from "../../widgets/LayoutToolbar";
import { ZoomControl } from "../../widgets/ZoomControl";
import { HOLDING_AREA_ID } from "../MultiHangar/HoldingArea";
import { StackStrategy } from "./GenerateParamSets";
import { HangarStack } from "./Hangar";
import { formatObstacles } from "./HangarLayout/utils";
import { HangarLeftPanel } from "./HangarLeftPanel";
import { HangarPrintLayout } from "./HangarPrintLayout";
import { useHangarAutocomplete } from "./useHangarAutocomplete";

import { useNavigate } from "react-router-dom";
import {
  CollapsibleRampActionCard,
  useLayout,
} from "../Ramp/CollapsibleRampActionCard";
import {
  CustomPlacementOptions,
  defaultCustomPlacementOptions,
} from "./CustomStackDialog";

export const HANGAR_BACKGROUND_COLORS = {
  reference: "#F4D2CD",
  testing: "#23967F",
};

type Props = {
  id: string;
  stacks: HangarStack[];
  setStacks: (stacks: HangarStack[]) => void;
  isReference?: boolean;
  isAlgorithmTesting?: boolean;
  // Direct props for hangar data (replaces useThisHangar)
  hangar: Hangar;
  setHangar: (hangar: Hangar) => void;
  history: {
    undo: () => void;
    redo?: () => void;
  };
  // Optional callback for scheduled view updates
  onScheduledUpdate?: () => void;
  // Flag to indicate if this is a scheduled view
  isScheduledView?: boolean;
  // Custom undo handler for scheduled views
  onUndo?: () => void;
  // Props for scheduled view functionality
  scheduledViewProps?: {
    tenantTripMap: Map<string, string>;
    placementService: PlacementService;
    onRemoveAircraft?: (tenantId: string, tripId: string) => void;
    currentTime: Date;
    onScheduledUpdate: () => void;
    onTimeChange?: (time: Date) => void;
    timeRange?: { start: Date; end: Date };
    allPlacements?: any[];
  };
};

export const HangarPresenterBase: React.FC<Props> = ({
  id,
  stacks,
  setStacks,
  isReference,
  isAlgorithmTesting = false,
  hangar,
  setHangar,
  history,
  onScheduledUpdate,
  isScheduledView = false,
  onUndo,
  scheduledViewProps,
}) => {
  const navigate = useNavigate();
  const { isDrawerOpen } = useLayout();
  const { hasPermission } = usePermissions();
  const { isMultiHangar } = useMultiHangarState();
  const [stackStrategy, setStackStrategy] = React.useState<StackStrategy>(
    StackStrategy.normal
  );
  const { activeFBO } = useActiveFBOs();
  const { towingEquipments: allTowingEquipments } = useTowingEquipmentState();
  const defaultTowingEquipmentId = allTowingEquipments.find(
    (t) => t.in_use && t.is_default
  )?.towing_equipment_id;
  // Hangar data now comes from props instead of useThisHangar hook
  const [width, setWidth] = React.useState<number>(700);
  const [customPlacementOptions, setCustomPlacementOptions] =
    React.useState<CustomPlacementOptions>(
      defaultCustomPlacementOptions(activeFBO, hangar)
    );

  React.useEffect(() => {
    setCustomPlacementOptions(defaultCustomPlacementOptions(activeFBO, hangar));
  }, [stackStrategy]);

  const div = React.useCallback(
    (node) => {
      if (node?.offsetWidth && node?.offsetHeight) {
        let maxUnplacedAircraftLength = hangar.stack.tenants
          .filter((t) => t.position.x === null)
          .map((t) => t.aircraft.length + 20)
          .reduce((a, b) => Math.max(a, b), 0);

        maxUnplacedAircraftLength = Math.max(maxUnplacedAircraftLength, 70);

        const feetToPixels =
          node?.offsetHeight / (hangar.depth + maxUnplacedAircraftLength);
        const height =
          node.offsetHeight - maxUnplacedAircraftLength * feetToPixels;
        setWidth((hangar.width * height) / hangar.depth);
      }
    },
    [isDrawerOpen]
  );

  const polygons = React.useMemo(
    () =>
      getStackPolygons(
        hangar?.stack,
        width,
        1,
        isMultiHangar,
        id === HOLDING_AREA_ID
      ),
    [width, hangar?.stack?.tenants, hangar?.stack?.movableObstacles]
  );

  const setStack = React.useCallback(
    (stack: StackType) => {
      setHangar({
        ...hangar,
        stack: {
          ...hangar.stack,
          ...stack,
        },
      });
    },
    [hangar, setHangar]
  );

  const paramSet: ParamSet = React.useMemo(() => {
    if (!hangar) {
      return null;
    }
    return {
      run_id: null,
      label: "base case",
      // slim down the hangar for the API call
      hangar: {
        id: hangar.id,
        width: hangar.width,
        depth: hangar.depth,
        left_door: hangar.left_door,
        right_door: hangar.right_door,
        garage_doors: hangar.garageDoors ?? [],
        bays: hangar.bays ?? [],
      },
      // slim down the tenant so it's just what we need to do the placement
      aircrafts_to_place: hangar.stack.tenants
        .filter(
          (t) => t.position.preferences?.indexOf(Preference.LOCKED) === -1
        )
        .map((tenant) => ({
          id: tenant.aircraft.id,
          placement_id: tenant.placement_id,
          angles_to_try: [],
          sectors_to_try: null,
          preferences: convertAircraftPreferencesToAlgorithmPreferences(
            tenant.position.preferences
          ),
        })),
      locked_aircrafts: hangar.stack.tenants
        .filter((t) => t.position.preferences?.indexOf(Preference.LOCKED) > -1)
        .map((tenant) => ({
          id: tenant.aircraft.id,
          placement_id: tenant.placement_id,
          x: tenant.position.x,
          y: tenant.position.y,
          angle: tenant.position.angle,
        })),
      obstacles: formatObstacles(hangar),
      options: {
        ...hangar.stack.options,
        ...customPlacementOptions,
      },
    };
  }, [hangar]);

  const autocomplete = useHangarAutocomplete(
    {
      hangar,
      defaultTowingEquipmentId,
      width,
      customPlacementOptions,
    },
    [hangar, polygons, customPlacementOptions, defaultTowingEquipmentId]
  );

  const positions = hangar?.stack?.tenants
    .map((t) => `${t.position.x},${t.position.y},${t.position.angle}`)
    .join("");

  useUpdateEffect(() => {
    setHangar({
      ...hangar,
      last_stacked: new Date().toISOString(),
    });
  }, [positions]);

  if (!hangar) {
    return null;
  }

  return (
    <AutocompleteResultsContainer>
      <Stack
        direction="row"
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <CollapsibleRampActionCard
          location={{ type: "hangar", ...hangar }}
          onClickBack={() => navigate("/hangars")}
          hideNavigation={isScheduledView}
          extendBorderUp={isScheduledView}
        >
          <HangarLeftPanel
            width={isDrawerOpen ? 400 : 125}
            hangar={hangar}
            setHangar={setHangar}
            stacks={stacks}
            setStacks={setStacks}
            stackStrategy={stackStrategy}
            setStackStrategy={setStackStrategy}
            paramSet={paramSet}
            customPlacementOptions={customPlacementOptions}
            setCustomPlacementOptions={setCustomPlacementOptions}
            isReference={isReference}
            isAlgorithmTesting={isAlgorithmTesting}
            readOnly={!hasPermission(Permission.MODIFY_LAYOUT)}
            disableAutoOpen={!!onScheduledUpdate}
            isScheduledView={isScheduledView}
            onAddTrip={
              scheduledViewProps?.onScheduledUpdate
                ? () => {
                    // For scheduled views, trigger refresh after trip creation
                    scheduledViewProps.onScheduledUpdate();
                  }
                : undefined
            }
            currentTime={scheduledViewProps?.currentTime}
            timeRange={scheduledViewProps?.timeRange}
            allPlacements={scheduledViewProps?.allPlacements}
            onRemoveAircraft={scheduledViewProps?.onRemoveAircraft}
            onRefresh={scheduledViewProps?.onScheduledUpdate}
            onTimeChange={scheduledViewProps?.onTimeChange}
          />
        </CollapsibleRampActionCard>

        <Stack
          direction="column"
          sx={{
            flex: 1,
            height: "100%",
            width: "100%",
          }}
        >
          <LayoutToolbar
            location={{ type: "hangar", ...hangar }}
            setLocation={({ type: _type, ...location }) => {
              setHangar({ ...hangar, ...location });
            }}
            stack={hangar.stack}
            setStack={setStack}
            setStacks={setStacks}
            isScheduledView={isScheduledView}
            scheduledViewProps={scheduledViewProps}
            onClickUndo={() => {
              if (isScheduledView && onUndo) {
                onUndo();
              } else {
                history && history.undo();
              }
            }}
            renderOnPrint={() => (
              <HangarPrintLayout hangar={hangar} stack={hangar.stack} />
            )}
          />
          <div
            ref={div}
            style={{
              flex: 1,
              // This stops the aircraft being dragged outside of this section
              // overflow: "scroll",
              position: "relative",
              height: "100%",
              margin: "20px",
            }}
          >
            <Layout
              width={width}
              location={{ type: "hangar", ...hangar }}
              stack={hangar?.stack}
              setStack={setStack}
              autocomplete={autocomplete}
              onScheduledUpdate={onScheduledUpdate}
              scheduledViewProps={scheduledViewProps}
              options={{
                hideUnplaced: false,
                readOnly: false,
                frozen: !hasPermission(Permission.MODIFY_LAYOUT),
                printable: false,
                selectable: false,
                disableGrabScroll: isMultiHangar,
              }}
            />
          </div>
        </Stack>
        <ZoomControl
          currentZoom={width}
          increment={0.1}
          onClickPlus={() => setWidth(width * 1.1)}
          onClickMinus={() => setWidth(width * 0.9)}
        />
      </Stack>
    </AutocompleteResultsContainer>
  );
};
